import { OFFSET_DEFAULT } from 'app/shared/constants';

import { rulesSearch } from 'app/modules/search/actions';
import { selectHasReadRulesPermission } from 'app/modules/session/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef } from 'react';

import { FilterMoreDisplay } from 'app/modules/filters/components/FilterMoreDisplay';
import {
  searchRule,
  useRuleSearch,
} from 'app/modules/rules/queries/useRuleSearch';
import { selectSearchV2Enabled } from 'app/shared/featureFlags/selectors';
import { RuleSearchPayload } from 'app/modules/search/payload';
import { MatchType, RuleSearchOptions } from 'app/modules/search/enum';
import { useQueryClient } from '@tanstack/react-query';
import { RULE_QUERY_KEYS } from 'app/modules/rules/queries/keys';

interface Props {
  ids: number[];
}

export const RuleFilterDisplay = (props: Props) => {
  const { ids } = props;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // using search/selectors caused an import cycle
  const rulesV1 = useSelector((state: RootState) => state.search.rules.data);
  const hasReadRulesPermission = useSelector(selectHasReadRulesPermission);
  const isSearchV2Enabled = useSelector(selectSearchV2Enabled);

  const { rules: rulesV2 } = useRuleSearch();

  const rules = useMemo(
    () => (isSearchV2Enabled ? rulesV2 : rulesV1),
    [isSearchV2Enabled, rulesV1, rulesV2],
  );

  const rulesMap = useMemo(
    () => rules.reduce((acc, i) => ({ ...acc, [i.id]: i }), {}),
    [rules],
  );

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [ids]);

  useEffect(() => {
    if (hasReadRulesPermission) {
      const missingValues = ids.filter((i) => !rulesMap[i]);
      if (missingValues.length && !calledRef.current) {
        calledRef.current = true;

        if (isSearchV2Enabled) {
          const initialPayload: RuleSearchPayload = {
            offset: OFFSET_DEFAULT,
            limit: missingValues.length,
            search_conditions: [
              {
                field: RuleSearchOptions.ID,
                match_type: MatchType.EXACT,
                phrases: missingValues.map(String),
              },
            ],
          };

          queryClient.prefetchQuery({
            queryKey: RULE_QUERY_KEYS.getRuleSearch(initialPayload),
            queryFn: () => searchRule(initialPayload),
          });
        } else {
          dispatch(
            rulesSearch({
              ids: missingValues,
              limit: missingValues.length,
              offset: OFFSET_DEFAULT,
              phrase: '',
            }),
          );
        }
      }
    }
  }, [
    dispatch,
    hasReadRulesPermission,
    ids,
    isSearchV2Enabled,
    queryClient,
    rulesMap,
  ]);

  const values = useMemo(
    () => ids.map((i) => rulesMap[i]?.title || i),
    [ids, rulesMap],
  );

  return (
    <>
      <b>Rule</b> is one of <FilterMoreDisplay values={values} />
    </>
  );
};
